'use client';

import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { addDays, addMonths, subMonths, format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isBefore, startOfDay } from 'date-fns';

interface TimeSlot {
  id: string;
  time: string;
  available: boolean;
}

interface BookingCalendarProps {
  onDateSelect: (date: Date, time: string) => void;
  blockDurationMinutes: number;
  bufferMinutes: number;
  title?: string;
}

export function BookingCalendar({ onDateSelect, blockDurationMinutes, bufferMinutes, title }: BookingCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  // Mock availability - 70% of slots are available
  const getTimeSlots = (date: Date): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    // Business hours: 8 AM to 6 PM
    for (let hour = 8; hour < 18; hour++) {
      const timeString = `${hour.toString().padStart(2, '0')}:00`;
      // Mock: Random availability
      const available = Math.random() > 0.3;
      slots.push({
        id: timeString,
        time: timeString,
        available
      });
    }
    return slots;
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    if (selectedDate) {
      onDateSelect(selectedDate, time);
    }
  };

  const timeSlots = selectedDate ? getTimeSlots(selectedDate) : [];

  const isDateDisabled = (date: Date) => {
    return isBefore(startOfDay(date), startOfDay(new Date())) || !isSameMonth(date, currentMonth);
  };

  const getDaysInWeek = (days: Date[]) => {
    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];

    days.forEach((day) => {
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      currentWeek.push(day);
    });

    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    return weeks;
  };

  const weeks = getDaysInWeek(days);

  return (
    <div data-zylo-id="zylo-7dadbce461a1" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="89:5-196:11" data-zylo-component="BookingCalendar" className="space-y-6">
      {title && <h3 data-zylo-id="zylo-1895585018f3" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="90:17-90:64" data-zylo-component="BookingCalendar" className="text-2xl font-bold">{title}</h3>}

      {/* Calendar */}
      <Card data-zylo-id="zylo-a8681e6e6791" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="93:7-153:14" data-zylo-component="BookingCalendar" className="p-6 border-0 shadow-lg">
        {/* Header with Navigation */}
        <div data-zylo-id="zylo-1b8bbe450188" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="95:9-113:15" data-zylo-component="BookingCalendar" className="flex items-center justify-between mb-6">
          <h4 data-zylo-id="zylo-f26d396e440a" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="96:11-96:85" data-zylo-component="BookingCalendar" className="text-lg font-bold">{format(currentMonth, 'MMMM yyyy')}</h4>
          <div data-zylo-id="zylo-02de796f2a99" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="97:11-112:17" data-zylo-component="BookingCalendar" className="flex gap-2">
            <Button data-zylo-id="zylo-5ed9824f8c18" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="98:13-104:22" data-zylo-component="BookingCalendar"
              variant="outline"
              size="icon"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            >
              <ChevronLeft data-zylo-id="zylo-dd32af4ea40a" data-zylo-kind="icon" data-zylo-cap="86" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="103:15-103:50" data-zylo-component="BookingCalendar" className="w-4 h-4" />
            </Button>
            <Button data-zylo-id="zylo-460632774b82" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="105:13-111:22" data-zylo-component="BookingCalendar"
              variant="outline"
              size="icon"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              <ChevronRight data-zylo-id="zylo-7703b75ddc6e" data-zylo-kind="icon" data-zylo-cap="86" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="110:15-110:51" data-zylo-component="BookingCalendar" className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Day Headers */}
        <div data-zylo-id="zylo-d4b51cdb7628" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="116:9-122:15" data-zylo-component="BookingCalendar" className="grid grid-cols-7 gap-2 mb-4">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div data-zylo-id="zylo-bc2c6196e1c6" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="118:13-120:19" data-zylo-component="BookingCalendar" key={day} className="text-center text-sm font-semibold text-muted-foreground">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div data-zylo-id="zylo-05c560b8fb46" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="125:9-152:15" data-zylo-component="BookingCalendar" className="space-y-2">
          {weeks.map((week, weekIndex) => (
            <div data-zylo-id="zylo-c3f9e8ded11a" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="127:13-150:19" data-zylo-component="BookingCalendar" key={weekIndex} className="grid grid-cols-7 gap-2">
              {week.map((date) => {
                const disabled = isDateDisabled(date);
                const isSelected = selectedDate && format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');

                return (
                  <motion.button data-zylo-id="zylo-46e63265c013" data-zylo-kind="button" data-zylo-cap="126" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="133:19-147:35" data-zylo-component="BookingCalendar"
                    key={format(date, 'yyyy-MM-dd')}
                    onClick={() => !disabled && handleDateSelect(date)}
                    disabled={disabled}
                    whileHover={!disabled ? { scale: 1.05 } : {}}
                    className={`p-2 text-sm font-semibold rounded-lg transition-all ${
                      disabled
                        ? 'opacity-30 cursor-not-allowed'
                        : isSelected
                          ? 'bg-accent text-white'
                          : 'bg-gray-100 hover:bg-accent/20 text-foreground'
                    }`}
                  >
                    {format(date, 'd')}
                  </motion.button>
                );
              })}
            </div>
          ))}
        </div>
      </Card>

      {/* Time Slots */}
      {selectedDate && (
        <motion.div data-zylo-id="zylo-c71317ab46c7" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="157:9-194:22" data-zylo-component="BookingCalendar"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card data-zylo-id="zylo-bbdda35987b5" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="161:11-193:18" data-zylo-component="BookingCalendar" className="p-6 border-0 shadow-lg">
            <h4 data-zylo-id="zylo-de7ce5b87894" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="162:13-164:18" data-zylo-component="BookingCalendar" className="text-lg font-bold mb-4">
              Available Times for {format(selectedDate, 'MMMM dd, yyyy')}
            </h4>
            <div data-zylo-id="zylo-1ce3d7fe056e" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="165:13-183:19" data-zylo-component="BookingCalendar" className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {timeSlots.map((slot) => (
                <motion.button data-zylo-id="zylo-b8050f20d9c2" data-zylo-kind="button" data-zylo-cap="126" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="167:17-181:33" data-zylo-component="BookingCalendar"
                  key={slot.id}
                  onClick={() => slot.available && handleTimeSelect(slot.time)}
                  disabled={!slot.available}
                  whileHover={slot.available ? { scale: 1.05 } : {}}
                  className={`p-3 text-sm font-semibold rounded-lg transition-all ${
                    !slot.available
                      ? 'bg-gray-100 text-muted-foreground opacity-50 cursor-not-allowed'
                      : selectedTime === slot.time
                        ? 'bg-accent text-white'
                        : 'bg-blue-50 border-2 border-blue-200 text-primary hover:bg-blue-100'
                  }`}
                >
                  {slot.time}
                </motion.button>
              ))}
            </div>
            {selectedTime && (
              <motion.p data-zylo-id="zylo-7404bdf2363e" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/components/booking-calendar.tsx" data-zylo-loc="185:15-191:26" data-zylo-component="BookingCalendar"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-4 text-sm text-accent font-semibold"
              >
                ✓ {format(selectedDate, 'MMMM dd, yyyy')} at {selectedTime} selected
              </motion.p>
            )}
          </Card>
        </motion.div>
      )}
    </div>
  );
}
